import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { User, ShoppingBag, Heart, Award, Clock, ArrowLeft, MapPin, RefreshCw, Trash2, LogOut, Star, Gift, Package, Truck, CheckCircle, Home, ShoppingCart } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';

const TIER_COLORS = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#b56b38', platinum: '#E5E4E2' };
const TIER_LABELS = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum' };
const STATUS_FLOW = ['confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered'];

export default function CustomerAccount() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [activeOrder, setActiveOrder] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      api.get('/orders/active').then(r => setActiveOrder(r.data.order)).catch(() => {});
    }
  }, [user]);

  if (authLoading || !user) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0E14] pb-16" data-testid="customer-account">
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/10 h-14">
        <div className="h-full max-w-5xl mx-auto px-4 flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> Shop</Link>
          <span className="font-['Playfair_Display'] text-lg text-white">My Account</span>
          <button onClick={() => { logout(); navigate('/'); }} className="text-[#64748B] hover:text-white" data-testid="account-logout"><LogOut className="w-4 h-4" /></button>
        </div>
      </nav>

      <div className="pt-16 pb-4 max-w-5xl mx-auto px-4">
        {/* Active Order Banner */}
        {activeOrder && (
          <Link to={`/order/${activeOrder.id}`} className="block bg-[#cc3737]/10 border border-[#cc3737]/30 rounded-xl p-4 mb-4 animate-pulse-glow" data-testid="active-order-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-[#cc3737]" />
                <div>
                  <p className="text-white font-medium text-sm">Order {activeOrder.order_number} — {activeOrder.status.replace(/_/g, ' ')}</p>
                  <p className="text-[#94A3B8] text-xs">{activeOrder.items?.length} items &middot; ${activeOrder.total?.toFixed(2)}</p>
                </div>
              </div>
              <span className="text-[#cc3737] text-sm font-medium">Track &rarr;</span>
            </div>
          </Link>
        )}

        {/* Profile Header */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-4 mb-4 flex items-center gap-4" data-testid="profile-header">
          <div className="w-12 h-12 rounded-full bg-[#cc3737]/20 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-[#cc3737]" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-medium truncate">{user.name}</h1>
            <p className="text-[#94A3B8] text-sm truncate">{user.phone}{user.email ? ` · ${user.email}` : ''}</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#12151C] border border-white/10 rounded-xl h-11 mb-4 p-1 w-full justify-start overflow-x-auto">
            {[
              { key: 'orders', icon: ShoppingBag, label: 'Orders' },
              { key: 'loyalty', icon: Award, label: 'Rewards' },
              { key: 'wishlist', icon: Heart, label: 'Wishlist' },
              { key: 'addresses', icon: MapPin, label: 'Addresses' },
              { key: 'profile', icon: User, label: 'Profile' },
            ].map(t => (
              <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-[#cc3737]/20 data-[state=active]:text-white text-[#64748B] rounded-lg px-3 gap-1.5 text-xs" data-testid={`account-tab-${t.key}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="loyalty"><LoyaltyTab /></TabsContent>
          <TabsContent value="wishlist"><WishlistTab /></TabsContent>
          <TabsContent value="addresses"><AddressesTab /></TabsContent>
          <TabsContent value="profile"><ProfileTab /></TabsContent>
        </Tabs>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0B0E14] border-t border-white/10 sm:hidden" data-testid="mobile-nav">
        <div className="flex justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-0.5 text-[#64748B] hover:text-white"><Home className="w-5 h-5" /><span className="text-[9px]">Home</span></Link>
          <Link to="/shop" className="flex flex-col items-center gap-0.5 text-[#64748B] hover:text-white"><ShoppingBag className="w-5 h-5" /><span className="text-[9px]">Shop</span></Link>
          <Link to="/checkout" className="flex flex-col items-center gap-0.5 text-[#64748B] hover:text-white"><ShoppingCart className="w-5 h-5" /><span className="text-[9px]">Cart</span></Link>
          <Link to="/account" className="flex flex-col items-center gap-0.5 text-[#cc3737]"><User className="w-5 h-5" /><span className="text-[9px]">Account</span></Link>
        </div>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => { fetchOrders(); }, []);
  const fetchOrders = async () => {
    try { const res = await api.get('/account/orders'); setOrders(res.data.orders); } catch {} finally { setLoading(false); }
  };

  const handleReorder = async (orderId) => {
    try {
      const res = await api.post(`/orders/${orderId}/reorder`);
      const items = res.data.items || [];
      if (items.length === 0) { toast.error('All items from this order are out of stock'); return; }
      items.forEach(item => addItem({ id: item.product_id, name: item.name, price: item.price, images: [item.image], sale_price: null, age_restricted: false }, item.quantity));
      const unavail = res.data.unavailable || [];
      if (unavail.length > 0) toast.warning(`Skipped (out of stock): ${unavail.slice(0, 3).join(', ')}${unavail.length > 3 ? ` +${unavail.length - 3} more` : ''}`);
      else toast.success('Items added to cart!');
      navigate('/checkout');
    } catch { toast.error('Failed to reorder'); }
  };

  const handleRefundRequest = async (orderId) => {
    const reason = window.prompt('Tell us briefly why you need a refund (min 5 chars):');
    if (reason === null) return;
    if (reason.trim().length < 5) { toast.error('Please give a reason of at least 5 characters'); return; }
    try {
      await api.post(`/orders/${orderId}/refund-request`, { reason: reason.trim() });
      toast.success('Refund request submitted. We\'ll review shortly.');
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit refund request'); }
  };

  const handleRate = async (orderId, rating) => {
    try { await api.post(`/orders/${orderId}/rate`, { rating }); toast.success('Thank you for rating!'); fetchOrders(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-[#12151C] rounded-xl h-24 animate-pulse" />)}</div>;

  return (
    <div data-testid="account-orders">
      <h2 className="font-['Playfair_Display'] text-xl text-white mb-4">Order History ({orders.length})</h2>
      {orders.length === 0 ? (
        <div className="text-center py-12 bg-[#12151C] border border-white/5 rounded-xl">
          <ShoppingBag className="w-8 h-8 text-[#64748B] mx-auto mb-3" />
          <p className="text-[#64748B]">No orders yet</p>
          <Link to="/shop" className="text-[#b56b38] text-sm hover:underline mt-2 inline-block">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order, i) => {
            const isActive = ['confirmed', 'preparing', 'ready', 'out_for_delivery'].includes(order.status);
            const statusIdx = STATUS_FLOW.indexOf(order.status);
            return (
              <div key={i} className={`bg-[#12151C] border rounded-xl p-4 ${isActive ? 'border-[#cc3737]/30' : 'border-white/10'}`} data-testid={`order-history-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[#b56b38] font-medium text-sm">{order.order_number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'delivered' ? 'bg-green-500/20 text-green-400' : order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>{order.status.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-[#64748B] text-xs">{new Date(order.created_at).toLocaleDateString()}</span>
                </div>

                {/* Mini status bar for active orders */}
                {isActive && (
                  <div className="flex gap-1 mb-3">
                    {STATUS_FLOW.slice(0, -1).map((s, j) => (
                      <div key={j} className={`h-1 flex-1 rounded-full ${j <= statusIdx ? 'bg-[#cc3737]' : 'bg-[#1A1D27]'}`} />
                    ))}
                  </div>
                )}

                {/* Items preview */}
                <div className="flex items-center gap-2 mb-3 overflow-x-auto">
                  {order.items?.slice(0, 4).map((item, j) => (
                    <div key={j} className="w-10 h-10 rounded bg-[#1A1D27] overflow-hidden flex-shrink-0">
                      {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                  ))}
                  {order.items?.length > 4 && <span className="text-[#64748B] text-xs">+{order.items.length - 4}</span>}
                  <span className="text-white font-medium ml-auto">${order.total?.toFixed(2)}</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {isActive && (
                    <Link to={`/order/${order.id}`} className="flex-1 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2 text-xs font-medium text-center transition-colors" data-testid={`track-order-${i}`}>Track Order</Link>
                  )}
                  {order.status === 'delivered' && !order.customer_rating && (
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(s => (
                        <button key={s} onClick={() => handleRate(order.id, s)} className="p-1" data-testid={`rate-${i}-${s}`}>
                          <Star className="w-4 h-4 text-[#64748B] hover:text-[#b56b38]" />
                        </button>
                      ))}
                    </div>
                  )}
                  {order.customer_rating && <span className="text-[#b56b38] text-xs flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> {order.customer_rating}/5</span>}
                  <button onClick={() => handleReorder(order.id)} className="text-[#b56b38] text-xs border border-[#b56b38]/30 rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-[#b56b38]/10 transition-colors" data-testid={`reorder-btn-${i}`}><RefreshCw className="w-3 h-3" /> Reorder</button>
                  {order.payment_status === 'paid' && !order.refund_status && ['delivered', 'cancelled'].includes(order.status) && (
                    <button onClick={() => handleRefundRequest(order.id)} className="text-[#94A3B8] hover:text-white text-xs border border-white/10 rounded-full px-3 py-1.5 transition-colors" data-testid={`refund-btn-${i}`}>Request Refund</button>
                  )}
                  {order.refund_status === 'requested' && <span className="text-yellow-400 text-xs px-2 py-1">Refund pending review</span>}
                  {order.refund_status === 'completed' && <span className="text-green-400 text-xs px-2 py-1">Refunded ${order.refund_amount?.toFixed(2)}</span>}
                </div>

                {/* Delivery/pickup photos */}
                {order.status === 'delivered' && order.delivery_photo && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <p className="text-[#64748B] text-xs mb-1">Delivery Proof</p>
                    <img src={order.delivery_photo} alt="Delivery" className="h-16 rounded object-cover" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LoyaltyTab() {
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/loyalty/status').then(r => setLoyalty(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="bg-[#12151C] rounded-xl h-48 animate-pulse" />;
  return (
    <div data-testid="account-loyalty">
      <h2 className="font-['Playfair_Display'] text-xl text-white mb-4">Loyalty Rewards</h2>
      {loyalty && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#12151C] to-[#1A1D27] border border-white/10 rounded-xl p-6" data-testid="loyalty-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8" style={{ color: TIER_COLORS[loyalty.tier] }} />
                <div>
                  <span className="text-sm font-medium" style={{ color: TIER_COLORS[loyalty.tier] }}>{TIER_LABELS[loyalty.tier]} Member</span>
                  <p className="text-white text-2xl font-semibold">{loyalty.points} pts</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[#64748B] text-xs">Redeemable</p>
                <p className="text-[#b56b38] font-medium text-lg">${loyalty.redeemable_value}</p>
              </div>
            </div>
            {loyalty.tier_info.next && (
              <div><div className="flex justify-between text-xs mb-1"><span className="text-[#94A3B8]">{loyalty.tier_info.next_at - loyalty.points} pts to {loyalty.tier_info.next}</span><span className="text-[#64748B]">{loyalty.progress}%</span></div><Progress value={loyalty.progress} className="h-2 bg-[#0B0E14]" /></div>
            )}
          </div>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Gift className="w-4 h-4 text-[#b56b38]" /> How It Works</h3>
            <div className="space-y-2 text-sm">
              <p className="text-[#94A3B8]"><span className="text-white font-medium">Earn:</span> 1 point per $1 spent on every order</p>
              <p className="text-[#94A3B8]"><span className="text-white font-medium">Redeem:</span> 100 points = $1 discount at checkout</p>
              <p className="text-[#94A3B8]"><span className="text-white font-medium">Tiers:</span> Bronze (0) → Silver (500) → Gold (1500) → Platinum (5000)</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(TIER_LABELS).map(([key, label]) => (
              <div key={key} className={`bg-[#12151C] border rounded-lg p-3 text-center ${loyalty.tier === key ? 'border-white/20' : 'border-white/5'}`}>
                <Award className="w-5 h-5 mx-auto mb-1" style={{ color: TIER_COLORS[key] }} />
                <p className="text-white text-xs font-medium">{label}</p>
                {loyalty.tier === key && <span className="text-[#10B981] text-[10px]">Current</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WishlistTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  useEffect(() => { api.get('/wishlist').then(r => setItems(r.data.items)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const removeWishlist = async (id) => { try { await api.delete(`/wishlist/${id}`); setItems(prev => prev.filter(p => p.id !== id)); toast.success('Removed'); } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); } };
  if (loading) return <div className="grid grid-cols-2 gap-3">{[1,2].map(i => <div key={i} className="bg-[#12151C] rounded-xl h-48 animate-pulse" />)}</div>;
  return (
    <div data-testid="account-wishlist">
      <h2 className="font-['Playfair_Display'] text-xl text-white mb-4">Wishlist ({items.length})</h2>
      {items.length === 0 ? (
        <div className="text-center py-12 bg-[#12151C] border border-white/5 rounded-xl"><Heart className="w-8 h-8 text-[#64748B] mx-auto mb-3" /><p className="text-[#64748B]">Empty wishlist</p><Link to="/shop" className="text-[#b56b38] text-sm hover:underline mt-2 inline-block">Browse products</Link></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((p, i) => (
            <div key={i} className="bg-[#12151C] border border-white/5 rounded-xl overflow-hidden" data-testid={`wishlist-item-${i}`}>
              <Link to={`/shop/product/${p.id}`}><div className="aspect-square bg-[#1A1D27]"><img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" /></div></Link>
              <div className="p-3"><p className="text-white text-sm font-medium line-clamp-1">{p.name}</p><p className="text-[#b56b38] text-sm mt-1">${(p.sale_price || p.price).toFixed(2)}</p>
                <div className="flex gap-2 mt-2"><button onClick={() => { addItem(p); toast.success('Added'); }} className="flex-1 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-1.5 text-xs font-medium">Add to Cart</button><button onClick={() => removeWishlist(p.id)} className="p-1.5 text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddressesTab() {
  const { user, setUser } = useAuth();
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [newAddr, setNewAddr] = useState('');

  const saveAddresses = async (updated) => {
    try { await api.put('/account/profile', { addresses: updated }); setAddresses(updated); setUser({ ...user, addresses: updated }); toast.success('Saved'); } catch { toast.error('Failed'); }
  };
  const addAddress = () => { if (!newAddr.trim()) return; const updated = [...addresses, newAddr.trim()]; saveAddresses(updated); setNewAddr(''); };
  const removeAddress = (idx) => { const updated = addresses.filter((_, i) => i !== idx); saveAddresses(updated); };

  return (
    <div data-testid="account-addresses">
      <h2 className="font-['Playfair_Display'] text-xl text-white mb-4">Saved Addresses</h2>
      <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 max-w-md space-y-3">
        {addresses.map((addr, i) => (
          <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-4 py-3">
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#cc3737]" /><span className="text-white text-sm">{addr}</span></div>
            <button onClick={() => removeAddress(i)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <input value={newAddr} onChange={e => setNewAddr(e.target.value)} placeholder="Add new delivery address" className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white text-sm focus:border-[#b56b38] focus:outline-none" data-testid="new-address-input" />
          <button onClick={addAddress} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-md px-4 text-sm font-medium" data-testid="add-address-btn">Add</button>
        </div>
      </div>
    </div>
  );
}

function ProfileTab() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  // Phone verification
  const [verifyState, setVerifyState] = useState('idle'); // idle | sent | done
  const [verifyCode, setVerifyCode] = useState('');
  const [devCode, setDevCode] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/account/profile', { name, phone }); setUser({ ...user, name, phone }); toast.success('Profile updated'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd) return toast.error('Both fields required');
    if (newPwd.length < 6) return toast.error('Min 6 characters');
    setPwdSaving(true);
    try { await api.put('/auth/change-password', { current_password: currentPwd, new_password: newPwd }); toast.success('Password changed!'); setCurrentPwd(''); setNewPwd(''); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setPwdSaving(false); }
  };
  const requestPhoneCode = async () => {
    try {
      const r = await api.post('/auth/verify-phone/request');
      setVerifyState('sent');
      if (r.data.dev_code) { setDevCode(r.data.dev_code); toast.info(`Dev code: ${r.data.dev_code}`); }
      else toast.success('Code sent — check your texts');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send code'); }
  };
  const confirmPhoneCode = async () => {
    if (!verifyCode.trim()) return toast.error('Enter the 4-digit code');
    try {
      await api.post('/auth/verify-phone/confirm', { code: verifyCode.trim() });
      setVerifyState('done');
      setUser({ ...user, phone_verified: true });
      toast.success('Phone verified ✓');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
  };

  return (
    <div data-testid="account-profile">
      <h2 className="font-['Playfair_Display'] text-xl text-white mb-4">Profile</h2>

      {/* Phone verification banner — only if not verified */}
      {!user?.phone_verified && verifyState !== 'done' && (
        <div className="bg-[#b56b38]/10 border border-[#b56b38]/30 rounded-xl p-4 mb-4 max-w-md" data-testid="verify-phone-banner">
          <p className="text-white text-sm font-medium mb-1">Verify your phone number</p>
          <p className="text-[#94A3B8] text-xs mb-3">Get instant SMS updates on your orders and unlock loyalty rewards faster.</p>
          {verifyState === 'idle' && (
            <button onClick={requestPhoneCode} className="bg-[#b56b38] hover:bg-[#d8964f] text-white rounded-full px-4 py-2 text-xs font-medium" data-testid="verify-phone-send-btn">Send code to {user?.phone}</button>
          )}
          {verifyState === 'sent' && (
            <div className="flex gap-2 items-center">
              <input value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0,4))} placeholder="1234" inputMode="numeric" maxLength={4} className="w-24 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-white text-sm font-mono text-center focus:border-[#b56b38] focus:outline-none" data-testid="verify-phone-code-input" />
              <button onClick={confirmPhoneCode} className="bg-[#10B981] hover:bg-[#0ea371] text-white rounded-full px-4 py-2 text-xs font-medium" data-testid="verify-phone-confirm-btn">Verify</button>
              <button onClick={requestPhoneCode} className="text-[#94A3B8] hover:text-white text-xs underline">Resend</button>
            </div>
          )}
        </div>
      )}
      <div className="space-y-4 max-w-md">
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 space-y-3">
          <div><label className="text-[#64748B] text-xs mb-1 block">Name</label><input value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white text-sm focus:border-[#b56b38] focus:outline-none" data-testid="profile-name-input" /></div>
          <div><label className="text-[#64748B] text-xs mb-1 block">Phone</label><input value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white text-sm focus:border-[#b56b38] focus:outline-none" data-testid="profile-phone-input" /></div>
          <div><label className="text-[#64748B] text-xs mb-1 block">Email</label><input value={email} disabled className="w-full bg-[#0B0E14] border border-white/5 rounded-md px-4 py-2.5 text-[#64748B] text-sm" /></div>
          <button onClick={handleSave} disabled={saving} className="bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full px-6 py-2.5 text-sm font-medium" data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-medium">Change Password</h3>
          <input value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} type="password" placeholder="Current password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white text-sm focus:border-[#b56b38] focus:outline-none" data-testid="customer-current-pwd" />
          <input value={newPwd} onChange={e => setNewPwd(e.target.value)} type="password" placeholder="New password (min 6)" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white text-sm focus:border-[#b56b38] focus:outline-none" data-testid="customer-new-pwd" />
          <button onClick={handleChangePassword} disabled={pwdSaving} className="bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full px-6 py-2.5 text-sm font-medium" data-testid="customer-change-pwd-btn">{pwdSaving ? 'Changing...' : 'Change Password'}</button>
        </div>
      </div>
    </div>
  );
}
